import React from "react";
import { Box, Alert, Text, Stack, useMantineTheme } from "@mantine/core";

export type ErrorType = "no-content" | "load-error";

export interface ErrorDisplayProps {
  /** Type of error to display */
  type: ErrorType;

  /** Additional CSS class name for styling */
  className?: string;

  /** Custom error message (optional, will use default if not provided) */
  message?: string;

  /** Custom error title (optional, will use default if not provided) */
  title?: string;
}

const ERROR_CONFIG: Record<
  ErrorType,
  { title: string; message: string; color: string }
> = {
  "no-content": {
    title: "No Content Blocks",
    message: "No content blocks are configured for this display.",
    color: "blue",
  },
  "load-error": {
    title: "Content Block Error",
    message: "Unable to load the current content block.",
    color: "red",
  },
};

export const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
  type,
  className,
  message,
  title,
}) => {
  const theme = useMantineTheme();
  const config = ERROR_CONFIG[type];

  const errorClassName = `content-block-area content-block-area--${
    type === "no-content" ? "empty" : "invalid"
  } ${className || ""}`;
  const alertClassName = `content-block-area__${
    type === "no-content" ? "empty" : "invalid"
  }-alert`;
  const messageClassName = `content-block-area__${
    type === "no-content" ? "empty" : "invalid"
  }-message`;

  return (
    <Box
      className={errorClassName}
      h="100%"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: theme.spacing.lg,
      }}
    >
      <Stack align="center" gap="md" maw={400}>
        <Alert
          color={config.color}
          title={title || config.title}
          className={alertClassName}
        >
          <Text size="sm" className={messageClassName} ta="center">
            {message || config.message}
          </Text>
        </Alert>
      </Stack>
    </Box>
  );
};
